import PropTypes from 'prop-types';
import React from 'react';
import Alert from 'src/components/alert';
import Feature from 'src/components/feature';
import Header from 'src/components/header';
import Loader from 'src/components/loader';
import { ConvertLoadButton } from 'src/components/lotToBatchConversion';
import Qr from 'src/components/qr';
import ResourceLookupField from 'src/components/resource-lookup-field';
import ResourceLookup from 'src/components/scan/lookup-resource';
import {
  API_RESOURCES,
  MATERIAL_BATCH_ACTIONS,
  PERMANENT_CONTAINER_ACTIONS,
} from 'src/utils/constants';
import { FEATURES } from 'src/utils/features';
import userPropType from 'src/utils/user-prop-type';

const QrScan = ({
  user,
  allowedEntityType,
  allowedResourcesToScan,
  successfulScan,
  handleScan,
  handleError,
  urlError,
  showInstructions,
  instructionText,
  requestedEntity,
  renderUseWithoutPermanentContainer,
  isUnloadAction,
  isActionMachineLoad,
  isLoadPermanentContainerAction,
  isLoadPrinterAction,
  initiatorAction,
  isSieveIntoPrinterContainerAction,
  isLoadAction,
  materialContainer,
  printerUri,
  showHeader,
  cameraWakeupStrategy,
  dataMatrixQrCodeScan,
}) => {
  const isMachineLoadAction =
    isLoadPrinterAction && requestedEntity === API_RESOURCES.MATERIAL_BATCH;
  const loadMachineResources = [API_RESOURCES.MATERIAL_CONTAINER, API_RESOURCES.PRINTER];
  const fillContainerResources = [API_RESOURCES.MATERIAL_CONTAINER, API_RESOURCES.MATERIAL_BATCH];
  const splitContainerResources = [API_RESOURCES.MATERIAL_CONTAINER, API_RESOURCES.PRINTER];
  const machineLoadResources = [API_RESOURCES.MATERIAL_BATCH, API_RESOURCES.MATERIAL_CONTAINER];
  const noRequestEntity = !requestedEntity;
  const batchRequestEntity = requestedEntity === API_RESOURCES.MATERIAL_BATCH;
  const containerRequestEntity = requestedEntity === API_RESOURCES.MATERIAL_CONTAINER;
  const printerRequestEntity = requestedEntity === API_RESOURCES.PRINTER;
  const isRunAction = requestedEntity === API_RESOURCES.RUN;
  const isContainerSplitAction = initiatorAction === PERMANENT_CONTAINER_ACTIONS.SPLIT_CONTAINER;
  const isBatchSplitAction = initiatorAction === MATERIAL_BATCH_ACTIONS.SPLIT_BATCH_CONTAINER;
  const shouldShowResourceLookupField =
    noRequestEntity || batchRequestEntity || containerRequestEntity || printerRequestEntity;
  const isContainerOnlyAction = [PERMANENT_CONTAINER_ACTIONS.SIEVE].includes(initiatorAction);

  const getResourceLookupData = () => {
    if ((isActionMachineLoad || isLoadAction) && !isLoadPrinterAction) {
      return loadMachineResources;
    }

    if (isMachineLoadAction) {
      return machineLoadResources;
    }

    if (isSieveIntoPrinterContainerAction) {
      return API_RESOURCES.PRINTER;
    }

    if (isUnloadAction || isContainerOnlyAction) {
      return API_RESOURCES.MATERIAL_CONTAINER;
    }

    if (isContainerSplitAction) {
      return splitContainerResources;
    }

    if (isLoadPermanentContainerAction) {
      return fillContainerResources;
    }

    if (isBatchSplitAction) {
      return API_RESOURCES.MATERIAL_CONTAINER;
    }

    if (batchRequestEntity) {
      return API_RESOURCES.MATERIAL_BATCH;
    }

    return [
      API_RESOURCES.MATERIAL_BATCH,
      API_RESOURCES.MATERIAL_CONTAINER,
      API_RESOURCES.PRINTER,
      API_RESOURCES.POST_PROCESSOR,
      API_RESOURCES.RUN,
      API_RESOURCES.PIECE,
      // Shipment must be last as partial look ups will fail for this resource.
      API_RESOURCES.SHIPMENT,
    ];
  };

  const resourceLookupName = getResourceLookupData();
  const shouldUseLookupResource = allowedResourcesToScan.length > 0;
  const renderUseWithoutPermanentContainerContent = renderUseWithoutPermanentContainer?.() || null;

  return (
    <>
      {showHeader && <Header scan title='Scan QR Code' user={user} />}

      <main role='main'>
        <div className='qr-reader text-white bg-dark rounded-sm overflow-hidden mb-3'>
          <Qr
            cameraWakeupStrategy={cameraWakeupStrategy}
            requestedEntity={allowedEntityType}
            isUnloadAction={isUnloadAction}
            hidden={successfulScan}
            isActionMachineLoad={isActionMachineLoad}
            dataMatrixQrCodeScan={dataMatrixQrCodeScan}
            onUrlError={urlError}
            onScan={handleScan}
            onError={handleError}
          >
            <Loader inline text='data' className='mt-3' />
          </Qr>
        </div>
        {urlError && (
          <Alert
            name='qr-error'
            variant='danger'
            className='text-center fs-mobile-14 p-mobile-5 mb-mobile-8'
          >
            {urlError.message}
          </Alert>
        )}
        {!urlError && showInstructions && (
          <Alert
            name='qr-instructions'
            variant='info'
            className='text-center fs-mobile-14 p-mobile-5 mb-mobile-8'
          >
            <span>{instructionText}</span>
          </Alert>
        )}
        {renderUseWithoutPermanentContainerContent}
        {shouldShowResourceLookupField && !isRunAction && (
          // It's possible to enter batch uuid if material management is enabled
          // and (
          //   exact resource type is not requested (user visited main page)
          //   or
          //   if expected to scan material batch entity
          // )
          <Feature featureName={FEATURES.MATERIAL_MANAGEMENT}>
            <div className='separator fs-mobile-14 mb-mobile-8'>OR</div>
            {shouldUseLookupResource ? (
              <ResourceLookup
                allowedResourcesToScan={allowedResourcesToScan}
                successfulScan={successfulScan}
                onScan={handleScan}
              />
            ) : (
              <ResourceLookupField
                resourceName={resourceLookupName}
                successfulScan={successfulScan}
                onScan={handleScan}
              />
            )}
          </Feature>
        )}
        {materialContainer && !materialContainer.current_batch && printerUri && (
          <ConvertLoadButton materialContainerId={materialContainer.uuid} printerUri={printerUri} />
        )}
      </main>
    </>
  );
};

QrScan.propTypes = {
  user: userPropType.isRequired,
  allowedEntityType: PropTypes.string,
  allowedResourcesToScan: PropTypes.arrayOf(PropTypes.string),
  requestedEntity: PropTypes.string,
  successfulScan: PropTypes.bool.isRequired,
  handleScan: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
  urlError: PropTypes.oneOfType([
    PropTypes.shape({
      message: PropTypes.string.isRequired,
    }),
    PropTypes.bool,
  ]),
  showInstructions: PropTypes.bool.isRequired,
  instructionText: PropTypes.string.isRequired,
  renderUseWithoutPermanentContainer: PropTypes.func,
  isUnloadAction: PropTypes.bool,
  isActionMachineLoad: PropTypes.bool,
  isLoadPrinterAction: PropTypes.bool,
  isLoadPermanentContainerAction: PropTypes.bool,
  isSieveIntoPrinterContainerAction: PropTypes.bool,
  isLoadAction: PropTypes.bool,
  initiatorAction: PropTypes.string,
  materialContainer: PropTypes.object,
  printerUri: PropTypes.string,
  showHeader: PropTypes.bool,
  cameraWakeupStrategy: PropTypes.oneOf(['reload', 'restart', false]),
  dataMatrixQrCodeScan: PropTypes.func,
};

QrScan.defaultProps = {
  allowedEntityType: null,
  allowedResourcesToScan: [],
  requestedEntity: null,
  renderUseWithoutPermanentContainer: null,
  isUnloadAction: false,
  isActionMachineLoad: false,
  isLoadPrinterAction: false,
  isLoadPermanentContainerAction: false,
  isSieveIntoPrinterContainerAction: false,
  isLoadAction: false,
  initiatorAction: '',
  materialContainer: null,
  printerUri: null,
  showHeader: true,
  cameraWakeupStrategy: 'reload',
  dataMatrixQrCodeScan: null,
};

export default QrScan;
